import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useUpdateProfilePayoutMutation } from '@/app/services/package-rtk.service';
import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

interface IProfileFormData {
  address1: string;
  address2: string;
  birthDate: string;
  city: string;
  companyName: string;
  country: string;
  firstName: string;
  lastName: string;
  phone: string;
  monthlyViews: string;
  textReaded: boolean;
  promotionPlan: string;
  ambitions: string;
  termsAndConditions: boolean;
  websiteLinks: string[];
  zipCode: string;
  isValidated: boolean;
  payoutInfo?: IPayoutInfo;
}

interface IPayoutInfo {
  payout: Payout;
  default: string;
}

interface Payout {
  methods: Methods;
}

interface Methods {
  bank: Bank;
}

interface Bank {
  fullname: string;
  iban: string;
  bankname: string;
  bicswiftcode: string;
}

type FormData = {
  PayoutMethod: 'bank';
  fullname: string;
  iban: string;
  bankname: string;
  bicswiftcode: string;
};

function Payout({ profile }: { profile: IProfileFormData }) {
  const { t } = useTranslation();

  const validationSchemaWithTranslation = Yup.object().shape({
    PayoutMethod: Yup.string()
      .oneOf(['bank'], t('BusinessPartner/Payout/invalidPayoutMethod') || 'Invalid Payout method')
      .required(t('BusinessPartner/Payout/payoutMethodRequired') || 'Payout method is required'),

    fullname: Yup.string()
      .nullable()
      .when('PayoutMethod', (PayoutMethod: any, schema) =>
        PayoutMethod[0] === 'bank' ? schema.required(t('BusinessPartner/Payout/fullnameRequired') || 'Full name is required') : schema
      ),

    iban: Yup.string()
      .nullable()
      .when('PayoutMethod', (PayoutMethod: any, schema) =>
        PayoutMethod[0] === 'bank' ? schema.required(t('BusinessPartner/Payout/ibanRequired') || 'IBAN is required') : schema
      ),

    bankname: Yup.string()
      .nullable()
      .when('PayoutMethod', (PayoutMethod: any, schema) =>
        PayoutMethod[0] === 'bank' ? schema.required(t('BusinessPartner/Payout/banknameRequired') || 'Bank name is required') : schema
      ),

    bicswiftcode: Yup.string()
      .nullable()
      .when('PayoutMethod', (PayoutMethod: any, schema) =>
        PayoutMethod[0] === 'bank' ? schema.required(t('BusinessPartner/Payout/bicswiftcodeRequired') || 'BIC/Swift code is required') : schema
      ),
  });

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    trigger,
    clearErrors,
    setValue,
    formState: { errors, isValid, isValidating },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchemaWithTranslation),
  });

  useEffect(() => {
    if (profile.payoutInfo) {
      setValue('PayoutMethod', profile.payoutInfo.default as any);
      setValue('bankname', profile.payoutInfo.payout.methods.bank.bankname);
      setValue('fullname', profile.payoutInfo.payout.methods.bank.fullname);
      setValue('iban', profile.payoutInfo.payout.methods.bank.iban);
      setValue('bicswiftcode', profile.payoutInfo.payout.methods.bank.bicswiftcode);
    }
  }, [profile]);

  const PayoutMethod = watch('PayoutMethod');

  const [updateProfilePayout, updateProfilePayoutStatus] = useUpdateProfilePayoutMutation();

  const onSubmit = (data: FormData) => {
    let payoutInfo: IPayoutInfo | undefined;

    if (profile?.payoutInfo) {
      payoutInfo = JSON.parse(JSON.stringify(profile?.payoutInfo));
    }

    if (!payoutInfo) {
      payoutInfo = {
        default: data.PayoutMethod,
        payout: {
          methods: {
            bank: {
              bankname: '',
              bicswiftcode: '',
              fullname: '',
              iban: '',
            },
          },
        },
      };
    }

    switch (data.PayoutMethod) {
      case 'bank':
        payoutInfo.payout.methods.bank.bankname = data.bankname;
        payoutInfo.payout.methods.bank.fullname = data.fullname;
        payoutInfo.payout.methods.bank.bicswiftcode = data.bicswiftcode;
        payoutInfo.payout.methods.bank.iban = data.iban;
        break;
      default:
        break;
    }

    const profileInfo = { ...profile };

    profileInfo.payoutInfo = payoutInfo;

    updateProfilePayout(profileInfo)
      .unwrap()
      .then(res => {
        toast.success(t('BusinessPartner/Payout/successEdit'));
      })
      .catch(err => {
        toast.error(err?.data?.message || err?.message);
      });
  };

  return (
    <div className="tw-w-full tw-max-w-2xl tw-mx-auto tw-px-6 tw-py-8 tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-100">
      <Form onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col tw-gap-6">
        <div className="tw-text-center">
          <h2 className="tw-text-gray-800 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-mb-2">{t('BusinessPartner/Payout/confirmMethod')}</h2>
          <p className="tw-text-gray-500 tw-text-sm tw-font-['Lexend']">
            {t('BusinessPartner/Payout/description') || 'Configure your payout method for receiving payments'}
          </p>
        </div>

        {/* Payment Method Selection */}
        <Form.Group className="tw-w-full tw-relative">
          <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend']">{t('BusinessPartner/Payout/howReceive')}</Form.Label>
          <div className="tw-relative">
            <Form.Select
              translate="no"
              {...register('PayoutMethod')}
              isInvalid={!!errors.PayoutMethod}
              className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md"
            >
              <option value="">{t('BusinessPartner/Payout/selectMethod')}</option>
              <option value="bank">{t('BusinessPartner/Payout/credit')}</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.PayoutMethod?.message}</Form.Control.Feedback>

            {/* Default Badge */}
            {watch('PayoutMethod') === profile.payoutInfo?.default && (
              <div className="tw-absolute tw-top-1/2 tw-right-4 -tw-translate-y-1/2 tw-bg-emerald-100 tw-rounded-md tw-px-2 tw-py-1">
                <span className="tw-text-emerald-800 tw-text-xs">{t('BusinessPartner/Payout/default')}</span>
              </div>
            )}
          </div>
        </Form.Group>

        {/* Bank Details Form Fields */}
        {PayoutMethod === 'bank' && (
          <>
            {['fullname', 'iban', 'bankname', 'bicswiftcode'].map((field: any, index) => (
              <Form.Group key={index} className="tw-w-full">
                <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend']">{t(`BusinessPartner/Payout/${field}`)}</Form.Label>
                <Form.Control
                  type="text"
                  {...register(field as any)}
                  isInvalid={!!errors[field]}
                  className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md"
                />
                <Form.Control.Feedback type="invalid">{errors[field]?.message}</Form.Control.Feedback>
              </Form.Group>
            ))}
          </>
        )}

        {/* Action Buttons */}
        <div className="tw-mt-4">
          <PSButton
            id="btn-payout-submit"
            type="submit"
            variant="primary"
            isLoading={updateProfilePayoutStatus.isLoading}
            title={t('BusinessPartner/Payout/confirm')}
            className="tw-w-full"
          />
        </div>
      </Form>
    </div>
  );
}

export default WrappedErrorBoundary(Payout);
